import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useBalances, useMyBookings, useMyParticipants } from "../lib/queries";
import { Empty, ErrorBox, Loading } from "../components/Loading";
import { fmtDayLabel, fmtTimeRange } from "../lib/format";

export default function Me() {
  const { user } = useAuth();
  const bookingsQ = useMyBookings(user?.parent_id ?? undefined);
  const kidsQ = useMyParticipants(user?.parent_id ?? undefined);

  if (bookingsQ.isLoading) return <Loading />;
  if (bookingsQ.error) return <ErrorBox message={(bookingsQ.error as Error).message} />;

  const future = (bookingsQ.data ?? []).filter((b: any) => new Date(b.session.starts_at) >= new Date());

  return (
    <div className="p-4 space-y-6">
      <h1 className="text-2xl font-bold">Мої записи</h1>

      {future.length === 0 ? (
        <Empty label="Поки немає майбутніх записів." />
      ) : (
        <ul className="space-y-2">
          {future.map((b: any) => (
            <li key={b.id} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">
                    {b.session.activity_type.emoji} {b.session.activity_type.name} — {b.participant.name}
                  </div>
                  <div className="text-tg-hint text-sm mt-0.5">
                    {fmtDayLabel(b.session.starts_at)} · {fmtTimeRange(b.session.starts_at, b.session.ends_at)}
                  </div>
                  {b.session.location_text && (
                    <div className="text-tg-hint text-sm mt-0.5">📍 {b.session.location_text}</div>
                  )}
                </div>
                <Link to={`/session/${b.session.id}`} className="btn-secondary text-sm">Відкрити</Link>
              </div>
            </li>
          ))}
        </ul>
      )}

      <section>
        <h2 className="text-lg font-semibold mb-2">Баланси</h2>
        {(kidsQ.data ?? []).length === 0 ? (
          <Empty label="Додайте дитину, щоб бачити абонементи." />
        ) : (
          <div className="space-y-3">
            {(kidsQ.data ?? []).map((k) => <KidBalances key={k.id} participantId={k.id} name={k.name} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function KidBalances({ participantId, name }: { participantId: string; name: string }) {
  const q = useBalances(participantId);
  const balances = q.data ?? [];
  return (
    <Link to={`/me/kid/${participantId}`} className="card block active:scale-[0.99] transition">
      <div className="flex justify-between items-baseline">
        <div className="font-semibold">{name}</div>
        <div className="text-tg-link text-sm">Деталі →</div>
      </div>
      {balances.length === 0 ? (
        <div className="text-tg-hint text-sm mt-1">Активних абонементів немає.</div>
      ) : (
        <ul className="space-y-1 text-sm mt-1">
          {balances.map((b) => {
            const debt = b.remaining < 0;
            const activity = b.plan?.activity_type;
            return (
              <li key={b.id} className="flex justify-between">
                <span>{activity?.emoji ?? ""} {b.note ?? activity?.name ?? "Абонемент"}</span>
                <span className={`font-medium tabular-nums ${debt ? "text-amber-700" : ""}`}>
                  {debt ? `${b.remaining} борг` : `${b.remaining} зан.`}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Link>
  );
}
